import { useState } from "react";
import { CalendarDays, MapPin, Video, MessageCircle, Phone, Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { EMIRATES } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SubmitBar } from "../components/SubmitBar";

export default function Consultation() {
  const { t, lang } = useLang();
  const c = t.consultation;

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    project_stage: "concept",
    visit_type: "site_visit",
    emirate: "dubai",
    address: "",
    preferred_date: "",
    preferred_time: "morning",
    notes: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const payload = {
    name: form.name,
    phone: form.phone,
    project_type: "restaurant",
    emirate: form.emirate,

    company_name: "",

    email: form.email,

    preferred_contact: "call",

    message:
      form.notes ||
      `Consultation booking (${form.visit_type}) — Stage: ${form.project_stage}`,
};
  const extra = {
    project_stage: form.project_stage,
    visit_type: form.visit_type,
    address: form.address,
    preferred_date: form.preferred_date,
    preferred_time: form.preferred_time,
    notes: form.notes,
  };

  return (
    <div data-testid="consultation-page">
      {/* Hero */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(#0891b2 1px, transparent 1px), linear-gradient(90deg, #0891b2 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] text-[#ef4444] font-semibold mb-4 uppercase">
                <CalendarDays className="w-3.5 h-3.5" />
                {c.eyebrow}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 text-[#0891b2]">
                {c.title}
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">{c.subtitle}</p>
              <ul className="space-y-2.5">
                {c.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-green-700" />
                    </div>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            {/* Right visual — appointment card */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#0891b2] to-[#0f172a] text-white rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center">
                    <CalendarDays className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-200">{c.demoLabel1}</div>
                    <div className="font-display text-lg">{c.demoValue1}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-5">
                  {["MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d, i) => (
                    <div
                      key={d}
                      className={`text-center py-3 rounded-lg text-xs font-semibold ${
                        i === 2 ? "bg-[#ef4444] text-white" : "bg-white/5 text-white/60"
                      }`}
                    >
                      <div className="text-[9px] tracking-widest">{d}</div>
                      <div className="text-lg mt-0.5">{18 + i}</div>
                    </div>
                  ))}
                </div>
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm">
                      <div className="font-semibold">{c.demoLabel2}</div>
                      <div className="text-xs text-white/60">{c.demoValue2}</div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating stat */}
              <div className="absolute -bottom-5 -start-4 lg:-start-8 bg-white shadow-xl rounded-xl px-5 py-3 border border-slate-100">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.demoLabel3}</div>
                <div className="font-display text-2xl text-[#ef4444]">{c.demoValue3}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-6">
            {c.steps.map((s, i) => (
              <div key={i} data-testid={`cons-step-${i}`} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="text-[70px] font-display leading-none text-[#0891b2]/10 mb-2">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-lg text-[#0891b2] mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="book" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#0891b2] to-[#0f172a] text-white p-6">
              <div className="inline-block text-[10px] tracking-[0.3em] text-red-300 font-semibold mb-2 uppercase">
                {c.formEyebrow}
              </div>
              <h2 className="font-display text-2xl">{c.formTitle}</h2>
              <p className="text-white/70 text-sm mt-1">{c.formSubtitle}</p>
            </div>
            <div className="p-6 sm:p-8 space-y-5">
              <Field label={c.visitType} required>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "site_visit", icon: MapPin },
                    { key: "video_call", icon: Video },
                    { key: "phone_call", icon: Phone },
                  ].map(({ key, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => set("visit_type", key)}
                      data-testid={`cons-vtype-${key}`}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-lg text-xs font-medium transition-all border ${
                        form.visit_type === key
                          ? "bg-[#0891b2] text-white border-[#0891b2] shadow-md"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#0891b2] hover:text-[#0891b2]"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {c.visitTypes[key]}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={c.name} required>
                  <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cons-name" className="h-11" />
                </Field>
                <Field label={c.phone} required>
                  <Input required dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="cons-phone" className="h-11" placeholder="+971 4-288-2777" />
                </Field>
              </div>
              <Field label={c.email}>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cons-email" className="h-11" />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={c.projectStage} required>
                  <Select value={form.project_stage} onValueChange={(v) => set("project_stage", v)}>
                    <SelectTrigger data-testid="cons-stage" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["idea", "concept", "design", "build", "operating"].map((k) => (
                        <SelectItem key={k} value={k}>{c.projectStages[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={c.emirate} required>
                  <Select value={form.emirate} onValueChange={(v) => set("emirate", v)}>
                    <SelectTrigger data-testid="cons-emirate" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EMIRATES.map((e) => <SelectItem key={e} value={e}>{t.emirates[e]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {form.visit_type === "site_visit" && (
                <Field label={c.address}>
                  <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="cons-address" className="h-11" placeholder={c.addressHint} />
                </Field>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={c.preferredDate}>
                  <Input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="cons-date" className="h-11" />
                </Field>
                <Field label={c.preferredTime}>
                  <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
                    <SelectTrigger data-testid="cons-time" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["morning", "afternoon", "evening"].map((k) => (
                        <SelectItem key={k} value={k}>{c.times[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label={c.notes}>
                <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="cons-notes" placeholder={c.notesHint} />
              </Field>

              <div className="pt-4 border-t border-slate-100">
                <SubmitBar
    payload={payload}
    source="quotation"
    submitLabel={c.submit}
    extraData={{
        ...extra,
        source: "consultation_booking",
    }}
/>
                <p className="text-xs text-slate-500 mt-3">{c.formFooter}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
